import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Tabs, Table, Select, Typography, Tag, Empty, Space, Row, Col, Button, Modal, message, Tooltip, Alert } from 'antd';
import { TrophyOutlined, CrownOutlined, SendOutlined, WhatsAppOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

const METRICS = [
  { key: 'goals',        label: '⚽ Artilharia',   totalLabel: 'Gols' },
  { key: 'assists',      label: '🅰️ Assistências', totalLabel: 'Assist.' },
  { key: 'yellow_cards', label: '🟨 Amarelos',     totalLabel: 'Amarelos' },
  { key: 'blue_cards',   label: '🟦 Azuis',        totalLabel: 'Azuis' },
  { key: 'red_cards',    label: '🟥 Vermelhos',    totalLabel: 'Vermelhos' },
  { key: 'fouls',        label: '🚨 Faltas',       totalLabel: 'Faltas' },
];

function fmtCooldown(sec) {
  if (sec <= 0) return 'agora';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

const Rankings = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [activeTab, setActiveTab] = useState('goals');
  const [rankings, setRankings] = useState({}); // { goals: [...], assists: [...] }
  const [loading, setLoading] = useState(false);

  // Broadcast
  const [broadcastStatus, setBroadcastStatus] = useState({ can_send: true, wait_seconds: 0 });
  const [previewModal, setPreviewModal] = useState({ open: false, text: '', size: 0 });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const countdownRef = useRef(null);

  const loadYears = useCallback(async () => {
    try {
      const r = await api.get('/organizer/rankings/years');
      setYears(r.data?.years || [new Date().getFullYear()]);
    } catch { /* silencioso */ }
  }, []);

  const loadRanking = useCallback(async (type, yr) => {
    setLoading(true);
    try {
      const r = await api.get('/organizer/rankings', { params: { type, year: yr } });
      setRankings(prev => ({ ...prev, [type]: r.data?.ranking || [] }));
    } catch {
      setRankings(prev => ({ ...prev, [type]: [] }));
    }
    setLoading(false);
  }, []);

  const loadBroadcastStatus = useCallback(async () => {
    try {
      const r = await api.get('/organizer/rankings/broadcast-status');
      setBroadcastStatus(r.data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { loadYears(); loadBroadcastStatus(); }, [loadYears, loadBroadcastStatus]);
  useEffect(() => { loadRanking(activeTab, year); }, [activeTab, year, loadRanking]);

  // Countdown local do cooldown
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (broadcastStatus.can_send || broadcastStatus.wait_seconds <= 0) return;
    countdownRef.current = setInterval(() => {
      setBroadcastStatus(prev => {
        const s = Math.max(0, (prev.wait_seconds || 0) - 1);
        if (s === 0) return { ...prev, wait_seconds: 0, can_send: true };
        return { ...prev, wait_seconds: s };
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [broadcastStatus.can_send, broadcastStatus.wait_seconds]);

  const openPreview = async () => {
    setPreviewLoading(true);
    setPreviewModal({ open: true, text: '', size: 0 });
    try {
      const r = await api.post('/organizer/rankings/broadcast', { type: activeTab, year, preview: true });
      setPreviewModal({ open: true, text: r.data?.text || '', size: r.data?.ranking_size || 0 });
    } catch (err) {
      message.error(err?.response?.data?.mensagem || 'Erro ao gerar preview');
      setPreviewModal({ open: false, text: '', size: 0 });
    }
    setPreviewLoading(false);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await api.post('/organizer/rankings/broadcast', { type: activeTab, year });
      message.success('Enviado no grupo!');
      setPreviewModal({ open: false, text: '', size: 0 });
      setBroadcastStatus({ can_send: false, wait_seconds: r.data?.cooldown_seconds || 21600 });
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'cooldown') {
        setBroadcastStatus({ can_send: false, wait_seconds: err.response.data.wait_seconds || 21600 });
        message.warning(err.response.data.mensagem);
      } else {
        message.error(err?.response?.data?.mensagem || 'Erro ao enviar');
      }
    }
    setSending(false);
  };

  const positionCell = (pos) => {
    if (pos === 1) return <span><CrownOutlined style={{ color: '#faad14', marginRight: 4 }} />1º</span>;
    if (pos === 2) return <Text strong style={{ color: '#8c8c8c' }}>2º</Text>;
    if (pos === 3) return <Text strong style={{ color: '#cd7f32' }}>3º</Text>;
    return <Text type="secondary">{pos}º</Text>;
  };

  const renderTable = (metric) => {
    const data = rankings[metric.key] || [];
    if (!loading && data.length === 0) {
      return <Empty description={`Sem dados de ${metric.totalLabel.toLowerCase()} em ${year}. Lance estatísticas nos jogos (ABERTOS em tempo real ou FECHADOS depois).`} />;
    }
    return (
      <Table
        dataSource={data}
        rowKey="player_id"
        pagination={false}
        loading={loading}
        size="middle"
        columns={[
          { title: 'Pos.', dataIndex: 'position', width: 70, align: 'center', render: (p) => positionCell(p) },
          { title: 'Jogador', dataIndex: 'name', render: (v) => <Text strong>{v}</Text> },
          { title: metric.totalLabel, dataIndex: 'total', align: 'right', width: 100, render: (v) => <Tag color="green" style={{ fontSize: 14 }}>{v}</Tag> },
          { title: 'Jogos', dataIndex: 'games', align: 'right', width: 90, render: (v) => <Text type="secondary">{v}</Text> },
          { title: 'Média/jogo', dataIndex: 'avg_per_game', align: 'right', width: 110, render: (v) => <Text>{Number(v).toFixed(2)}</Text> },
        ]}
      />
    );
  };

  const items = METRICS.map(m => ({
    key: m.key,
    label: m.label,
    children: renderTable(m),
  }));

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <TrophyOutlined style={{ color: '#faad14' }} /> Ranking
          </Title>
          <Text type="secondary">Artilharia, assistências e cartões do ano.</Text>
        </Col>
        <Col>
          <Space wrap>
            <Space size={6}>
              <Text strong>Ano:</Text>
              <Select
                value={year}
                onChange={setYear}
                style={{ width: 110 }}
                options={years.map(y => ({ value: y, label: String(y) }))}
              />
            </Space>
            <Tooltip
              title={broadcastStatus.can_send
                ? 'Envia o ranking da aba atual no grupo do WhatsApp'
                : `Disponível em ${fmtCooldown(broadcastStatus.wait_seconds)}`}
            >
              <Button
                type="primary"
                icon={<WhatsAppOutlined />}
                onClick={openPreview}
                disabled={!broadcastStatus.can_send}
                style={{
                  background: broadcastStatus.can_send ? '#25D366' : undefined,
                  borderColor: broadcastStatus.can_send ? '#25D366' : undefined,
                  fontWeight: 600,
                }}
              >
                {broadcastStatus.can_send
                  ? 'Enviar no grupo'
                  : `Disponível em ${fmtCooldown(broadcastStatus.wait_seconds)}`}
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={items}
          size="large"
          destroyInactiveTabPane
        />
      </Card>

      <Modal
        title={<span><SendOutlined /> Enviar ranking no grupo</span>}
        open={previewModal.open}
        onCancel={() => setPreviewModal({ open: false, text: '', size: 0 })}
        onOk={confirmSend}
        confirmLoading={sending}
        okText="Enviar agora"
        cancelText="Cancelar"
        okButtonProps={{
          disabled: previewLoading || previewModal.size === 0,
          style: { background: '#25D366', borderColor: '#25D366', fontWeight: 600 },
          icon: <WhatsAppOutlined />,
        }}
        width={560}
      >
        <Alert
          type="warning" showIcon
          message="Cooldown de 6 horas"
          description="Depois de enviar, você só poderá enviar outro ranking daqui 6 horas."
          style={{ marginBottom: 12, borderRadius: 10 }}
        />
        {previewLoading ? (
          <Empty description="Gerando preview…" />
        ) : previewModal.size === 0 ? (
          <Empty description="Sem dados desse ranking pra enviar." />
        ) : (
          <div style={{
            background: '#e8f5e9', border: '1px solid #b7eb8f', borderRadius: 12,
            padding: 16, fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap',
            maxHeight: 400, overflowY: 'auto',
          }}>
            {previewModal.text}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Rankings;
